import { Inject, Injectable, Optional } from '@angular/core';
import { PREP_TOUR_PROGRESS_ADAPTER, PrepTourProgressAdapter, PrepTourProgressSnapshot } from './tour.types';

export const EMPTY_TOUR_PROGRESS: Readonly<PrepTourProgressSnapshot> = Object.freeze({
  seen: [],
  completed: {},
  dismissed: [],
  lastStep: {},
});

/** Keeps progress for the session only (tests, demos). */
export class PrepTourMemoryProgressAdapter implements PrepTourProgressAdapter {
  private snapshot: PrepTourProgressSnapshot | null = null;
  load(): PrepTourProgressSnapshot | null { return this.snapshot; }
  save(s: PrepTourProgressSnapshot): void { this.snapshot = s; }
  clear(): void { this.snapshot = null; }
}

/** Default: `localStorage` under one key (the host picks the key/namespace). */
export class PrepTourLocalStorageProgressAdapter implements PrepTourProgressAdapter {
  constructor(private readonly key = 'prep-tour.progress') {}
  load(): PrepTourProgressSnapshot | null {
    try {
      const raw = localStorage.getItem(this.key);
      return raw ? (JSON.parse(raw) as PrepTourProgressSnapshot) : null;
    } catch {
      return null;
    }
  }
  save(s: PrepTourProgressSnapshot): void {
    try { localStorage.setItem(this.key, JSON.stringify(s)); } catch { /* quota / privacy mode */ }
  }
  clear(): void {
    try { localStorage.removeItem(this.key); } catch { /* ignore */ }
  }
}

/**
 * Seen / completed / dismissed state of every guide, persisted through the
 * {@link PREP_TOUR_PROGRESS_ADAPTER}. Loads once at first use; every mutation
 * is saved.
 */
@Injectable({ providedIn: 'root' })
export class PrepTourProgressStore {
  private readonly adapter: PrepTourProgressAdapter;
  private _snapshot: PrepTourProgressSnapshot = { ...EMPTY_TOUR_PROGRESS };
  private _loaded = false;

  constructor(@Optional() @Inject(PREP_TOUR_PROGRESS_ADAPTER) adapter: PrepTourProgressAdapter | null) {
    this.adapter = adapter || new PrepTourLocalStorageProgressAdapter();
    const result = this.adapter.load();
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<PrepTourProgressSnapshot | null>).then(
        (s) => { if (s) this._snapshot = normalize(s); this._loaded = true; },
        () => { this._loaded = true; },
      );
    } else {
      if (result) this._snapshot = normalize(result as PrepTourProgressSnapshot);
      this._loaded = true;
    }
  }

  get snapshot(): PrepTourProgressSnapshot { return this._snapshot; }
  /** False until the adapter answered (async adapters). */
  get loaded(): boolean { return this._loaded; }
  completedIds(): string[] { return Object.keys(this._snapshot.completed); }

  isSeen(guideId: string): boolean { return this._snapshot.seen.includes(guideId); }
  isDismissed(guideId: string): boolean { return this._snapshot.dismissed.includes(guideId); }
  /** Completed at this version (or any version when none is given). */
  isCompleted(guideId: string, version?: number): boolean {
    const v = this._snapshot.completed[guideId];
    return v !== undefined && (version === undefined || v >= version);
  }
  lastStep(guideId: string): number | undefined { return this._snapshot.lastStep[guideId]; }

  markSeen(guideId: string): void {
    this.update((s) => (s.seen.includes(guideId) ? s : { ...s, seen: [...s.seen, guideId] }));
  }
  markCompleted(guideId: string, version = 1): void {
    this.update((s) => ({
      ...s,
      completed: { ...s.completed, [guideId]: version },
      dismissed: s.dismissed.filter((id) => id !== guideId),
      lastStep: omit(s.lastStep, guideId),
    }));
  }
  dismiss(guideId: string): void {
    this.update((s) => (s.dismissed.includes(guideId) ? s : { ...s, dismissed: [...s.dismissed, guideId] }));
  }
  setLastStep(guideId: string, index: number): void {
    this.update((s) => ({ ...s, lastStep: { ...s.lastStep, [guideId]: index } }));
  }
  /** "Reset guidance": forget everything. */
  reset(): void {
    this._snapshot = { ...EMPTY_TOUR_PROGRESS };
    void this.adapter.clear();
  }

  private update(fn: (s: PrepTourProgressSnapshot) => PrepTourProgressSnapshot): void {
    const next = fn(this._snapshot);
    if (next === this._snapshot) return;
    this._snapshot = next;
    void this.adapter.save(next);
  }
}

function normalize(s: Partial<PrepTourProgressSnapshot>): PrepTourProgressSnapshot {
  return {
    seen: Array.isArray(s.seen) ? s.seen : [],
    completed: s.completed && typeof s.completed === 'object' ? s.completed : {},
    dismissed: Array.isArray(s.dismissed) ? s.dismissed : [],
    lastStep: s.lastStep && typeof s.lastStep === 'object' ? s.lastStep : {},
  };
}

function omit(obj: Record<string, number>, key: string): Record<string, number> {
  if (!(key in obj)) return obj;
  const rest: Record<string, number> = {};
  Object.keys(obj).forEach((k) => { if (k !== key) rest[k] = obj[k]; });
  return rest;
}
